<template>
  <div class="function-management-container">
    <!-- Diálogo de Edição Premium -->
    <v-dialog v-model="dialogoEditar" max-width="800px" persistent>
      <v-card class="edit-dialog-card elevation-8">
        <v-card-title class="dialog-header primary">
          <v-icon large left color="white">mdi-account-cog</v-icon>
          <span class="white--text">Editar Função</span>
          <v-chip color="white" text-color="primary" class="ml-3">
            {{ funcaoSelected.descricao }}
          </v-chip>
          <v-spacer></v-spacer>
          <v-btn icon dark @click="dialogoEditar = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-divider></v-divider>

        <v-card-text class="dialog-content">
          <!-- Seção de Permissões -->
          <v-container fluid class="permissions-section">
            <div class="section-title">
              <v-icon color="primary" class="mr-2">mdi-shield-account</v-icon>
              <span class="text-h6">Níveis de Permissão</span>
            </div>
            
            <v-chip-group column active-class="primary--text" class="permissions-grid">
              <v-chip v-for="(permissao, index) in permissoesAll" :key="index" 
                      class="permission-chip" color="blue lighten-5">
                <v-avatar left color="primary" class="permission-level">
                  {{ permissao.nivel }}
                </v-avatar>
                <span class="permission-desc">{{ permissao.descricao }}</span>
              </v-chip>
            </v-chip-group>
          </v-container>

          <!-- Formulário de Edição -->
          <v-form ref="form" v-model="valid" lazy-validation>
            <v-container class="form-container">
              <v-row>
                <v-col cols="12" md="6">
                  <v-text-field 
                    outlined 
                    label="Descrição" 
                    v-model="funcaoSelected.descricao"
                    :rules="[v => !!v || 'Campo obrigatório']"
                    prepend-inner-icon="mdi-text-box"
                    color="primary"
                  ></v-text-field>
                </v-col>

                <v-col cols="12" md="6">
                  <v-combobox 
                    outlined 
                    v-model="statusSelected" 
                    label="Status" 
                    item-value="id"
                    item-text="descricao" 
                    :items="statusOpcoes" 
                    return-object
                    prepend-inner-icon="mdi-toggle-switch"
                    color="primary"
                  ></v-combobox>
                </v-col>

                <v-col cols="12" md="6">
                  <v-text-field 
                    outlined 
                    label="Permissões" 
                    v-model="funcaoSelected.niveis"
                    :rules="[validateFormat]" 
                    @input="sanitizeInput"
                    hint="Números separados por vírgula (ex: 1,2,3)" 
                    persistent-hint
                    prepend-inner-icon="mdi-numeric"
                    color="primary"
                  ></v-text-field>
                </v-col>

                <v-col cols="12" md="6">
                  <v-text-field 
                    outlined 
                    label="Autor" 
                    v-model="funcaoSelected.modifiedBy"
                    disabled
                    prepend-inner-icon="mdi-account"
                  ></v-text-field>
                </v-col>

                <v-col cols="12" md="6">
                  <v-text-field 
                    outlined 
                    label="Última Atualização" 
                    :value="formatDateTime(funcaoSelected.modifiedOn)"
                    disabled
                    prepend-inner-icon="mdi-clock"
                  ></v-text-field>
                </v-col>
              </v-row>
            </v-container>
          </v-form>
        </v-card-text>

        <v-card-actions class="dialog-actions">
          <v-btn 
            large 
            text 
            @click="dialogoEditar = false"
            class="cancel-btn"
          >
            <v-icon left>mdi-close</v-icon>
            Cancelar
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn 
            large 
            color="primary" 
            @click="saveFunction" 
            :loading="saving" 
            :disabled="!valid"
            class="save-btn"
            depressed
          >
            <v-icon left>mdi-content-save</v-icon>
            Salvar Alterações
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Tabela de Funções Premium -->
    <v-card class="function-card elevation-3">
      <v-card-title class="table-header primary white--text">
        <v-icon large left color="white">mdi-account-group</v-icon>
        <span class="text-h5">Gerenciamento de Funções</span>
        <v-spacer></v-spacer>
        <v-text-field 
          v-model="search" 
          append-icon="mdi-magnify" 
          label="Pesquisar funções..." 
          single-line
          hide-details 
          outlined 
          dense 
          class="search-field"
          color="white"
          dark
          clearable
          style="max-width: 300px;"
        ></v-text-field>
      </v-card-title>

      <v-data-table 
        :headers="headers" 
        :items="funcoesMenu" 
        :search="search" 
        :loading="loading"
        loading-text="Carregando funções..." 
        no-data-text="Nenhuma função encontrada"
        no-results-text="Nenhum resultado para esta pesquisa" 
        class="function-data-table elevation-1"
        :items-per-page="10"
        :footer-props="{
          'items-per-page-options': [5, 10, 15, 20],
          'items-per-page-text': 'Itens por página:'
        }"
      >
        <template v-slot:item.index="{ index }">
          <v-chip color="primary" small dark>
            {{ index + 1 }}
          </v-chip>
        </template>

        <template v-slot:item.descricao="{ item }">
          <span class="function-name">{{ item.descricao }}</span>
        </template>

        <template v-slot:item.niveis="{ item }">
          <div class="permission-badges">
            <v-chip 
              v-for="nivel in item.niveis.split(',')" 
              :key="nivel" 
              small
              color="blue lighten-4" 
              class="mr-1 mb-1"
            >
              {{ nivel }}
            </v-chip>
          </div>
        </template>

        <template v-slot:item.status="{ item }">
          <v-chip 
            :color="item.status ? 'success' : 'error'" 
            small 
            dark
            :outlined="!item.status"
          >
            <v-icon left small>{{ item.status ? 'mdi-check-circle' : 'mdi-close-circle' }}</v-icon>
            {{ item.status ? 'Ativo' : 'Inativo' }}
          </v-chip>
        </template>

        <template v-slot:item.modifiedOn="{ item }">
          {{ formatDate(item.modifiedOn) }}
        </template>

        <template v-slot:item.action="{ item }">
          <v-menu offset-y left :close-on-content-click="false">
            <template v-slot:activator="{ on, attrs }">
              <v-btn 
                icon 
                v-bind="attrs"
                v-on="on"
                color="primary"
                class="action-btn"
              >
                <v-icon>mdi-dots-vertical</v-icon>
              </v-btn>
            </template>
            <v-list dense class="action-menu">
              <v-list-item @click="editPermissions(item)" class="menu-item">
                <v-list-item-icon>
                  <v-icon color="primary">mdi-pencil</v-icon>
                </v-list-item-icon>
                <v-list-item-title>Editar Função</v-list-item-title>
              </v-list-item>
              <v-divider></v-divider>
              <v-list-item @click="editUserExceptions(item)" class="menu-item">
                <v-list-item-icon>
                  <v-icon color="secondary">mdi-account-cog</v-icon>
                </v-list-item-icon>
                <v-list-item-title>Exceções de Usuário</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
        </template>

        <template v-slot:no-data>
          <div class="empty-state">
            <v-icon size="64" color="grey lighten-1">mdi-database-remove</v-icon>
            <div class="empty-text">Nenhuma função encontrada</div>
            <v-btn color="primary" class="mt-4" @click="initialize" depressed>
              <v-icon left>mdi-refresh</v-icon>
              Recarregar
            </v-btn>
          </div>
        </template>

        <template v-slot:no-results>
          <div class="empty-state">
            <v-icon size="64" color="grey lighten-1">mdi-magnify-remove</v-icon>
            <div class="empty-text">Nenhum resultado para "{{ search }}"</div>
            <v-btn color="primary" class="mt-4" @click="search = ''" depressed>
              <v-icon left>mdi-backspace</v-icon>
              Limpar pesquisa
            </v-btn>
          </div>
        </template>
      </v-data-table>
    </v-card>
  </div>
</template>

<script>
import funcoesUsuarioService from '@/services/funcoesUsuarioService';
import Swal from 'sweetalert2';
import funcaoUtilitarios from '@/utilitarios/funcaoUtilitarios'

export default {
  data: () => ({
    dadosRecebidos: null,
    funcoesMenu: [],
    funcaoSelected: {},
    permissoesAll: [],
    loading: false,
    saving: false,
    dialogoEditar: false,
    valid: true,
    search: '',
    statusSelected: null,
    statusOpcoes: [
      { id: 1, descricao: 'Ativo' },
      { id: 0, descricao: 'Inativo' }
    ],
    headers: [
      { text: "#", value: "index", width: '80px', align: 'center', sortable: false },
      { text: "DESCRIÇÃO", value: "descricao", width: '25%' },
      { text: "PERMISSÕES", value: "niveis", width: '25%' },
      { text: "STATUS", value: "status", width: '120px', align: 'center' },
      { text: "AUTOR", value: "modifiedBy", width: '20%' },
      { text: "ATUALIZAÇÃO", value: "modifiedOn", width: '150px' },
      { text: "AÇÕES", value: "action", width: '100px', align: 'center', sortable: false }
    ]
  }),

  created() {
    this.initialize();
  },

  computed: {
    usuario() {
      return require("local-storage").get('nomeUsuario');
    }
  },

  methods: {
    async initialize() {
      this.dadosRecebidos = this.$store.state.funcaoUsuarioMenuSelected;
      if (!this.dadosRecebidos) {
        this.$router.replace('/administracao/funcoes');
        return;
      }

      await Promise.all([
        this.loadMenuFunctions(this.dadosRecebidos.idMenu),
        this.loadPermissions()
      ]);
    },

    async loadMenuFunctions(menuId) {
      this.loading = true;
      try {
        this.funcoesMenu = await funcoesUsuarioService.getFuncoesMenu(menuId);
      } catch (error) {
        Swal.fire('Erro', 'Ocorreu um problema ao tentar carregar as funções. Por favor, tente novamente mais tarde.', 'error');
      } finally {
        this.loading = false;
      }
    },

    async loadPermissions() {
      try {
        this.permissoesAll = await funcoesUsuarioService.getPermissoesUsuarios();
      } catch (error) {
        this.showError('Erro ao carregar permissões', error);
      }
    },

    editPermissions(item) {
      this.statusSelected = this.statusOpcoes.find(s => s.id == item.status);
      this.funcaoSelected = { ...item };
      this.dialogoEditar = true;
    },

    async saveFunction() {
      if (!this.$refs.form.validate()) return;

      this.saving = true;
      try {
        const payload = {
          descricao: this.funcaoSelected.descricao,
          niveis: this.funcaoSelected.niveis,
          idFuncaoPed: this.funcaoSelected.idFuncaoPed,
          status: this.statusSelected.id,
          usuario: this.usuario
        };

        let update = await funcoesUsuarioService.updateFunction(payload);

        if (update == 200) {
          this.dialogoEditar = false;
          await funcaoUtilitarios.init(true);
          await this.loadMenuFunctions(this.dadosRecebidos.idMenu);
          Swal.fire({
            title: 'Sucesso',
            text: 'Função atualizada com sucesso!',
            icon: 'success',
            confirmButtonColor: '#1976D2',
            background: '#ffffff',
            backdrop: `
              rgba(41, 98, 255, 0.1)
            `
          });
        } else {
          Swal.fire({
            title: 'Erro',
            text: 'Ocorreu um erro ao atualizar a função',
            icon: 'error',
            confirmButtonColor: '#1976D2'
          });
        }
      } catch (error) {
        Swal.fire({
          title: 'Erro',
          text: 'Erro ao efetuar a operação',
          icon: 'error',
          confirmButtonColor: '#1976D2'
        });
      } finally {
        this.saving = false;
      }
    },

    editUserExceptions(item) {
      this.$store.state.funcaoExcecoesUsuario = item;
      this.$router.push('/administracao/excecoesFuncoes');
    },

    validateFormat(value) {
      const regex = /^(\d+)(,\d+)*$/;
      return regex.test(value) || "Formato inválido (use números separados por vírgula)";
    },

    sanitizeInput() {
      this.funcaoSelected.niveis = this.funcaoSelected.niveis
        .replace(/[^0-9,]/g, "")
        .replace(/,{2,}/g, ",")
        .replace(/^,|,$/g, "");
    },
    
    formatDate(dateString) {
      if (!dateString) return '';
      const options = { year: 'numeric', month: 'short', day: 'numeric' };
      return new Date(dateString).toLocaleDateString('pt-BR', options);
    },
    
    formatDateTime(dateString) {
      if (!dateString) return '';
      const options = { 
        year: 'numeric', 
        month: 'short', 
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      };
      return new Date(dateString).toLocaleDateString('pt-BR', options);
    }
  }
};
</script>

<style scoped>
.function-management-container {
  padding: 24px;
  width: 100%;
  margin: 0 auto;
  background-color: #f8f9fa;
}

.function-card {
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08) !important;
}

.table-header {
  padding: 16px 24px;
  background: linear-gradient(135deg, #1976D2 0%, #0D47A1 100%);
}

.search-field >>> .v-input__slot {
  background-color: rgba(255, 255, 255, 0.2) !important;
}

.search-field >>> .v-text-field__slot input {
  color: white !important;
}

.search-field >>> .v-input__append-inner .v-icon {
  color: white !important;
}

.function-data-table {
  border-top: 1px solid rgba(0, 0, 0, 0.05);
}

.function-data-table >>> thead tr {
  background-color: #f5f7fa;
}

.function-data-table >>> thead th {
  color: #2c3e50 !important;
  font-weight: 600 !important;
  font-size: 0.9rem;
  letter-spacing: 0.5px;
}

.function-data-table >>> tbody tr:nth-of-type(even) {
  background-color: rgba(25, 118, 210, 0.03);
}

.function-data-table >>> tbody tr:hover {
  background-color: rgba(25, 118, 210, 0.08) !important;
  transition: background-color 0.3s ease;
}

.function-name {
  font-weight: 500;
  color: #2c3e50;
}

.permission-badges {
  display: flex;
  flex-wrap: wrap;
}

/* Diálogo de Edição */
.edit-dialog-card {
  border-radius: 12px;
  overflow: hidden;
}

.dialog-header {
  padding: 16px 24px;
  color: white;
}

.dialog-content {
  padding: 24px;
}

.permissions-section {
  background-color: #f8fafc;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 24px;
  border: 1px solid #e2e8f0;
}

.section-title {
  display: flex;
  align-items: center;
  margin-bottom: 16px;
  color: #2c3e50;
}

.permissions-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 8px;
}

.permission-chip {
  padding: 4px 8px;
  height: auto;
}

.permission-level {
  font-size: 0.8rem;
  font-weight: bold;
  margin-right: 8px;
}

.permission-desc {
  font-size: 0.85rem;
}

.form-container {
  padding: 0;
}

.dialog-actions {
  padding: 16px 24px;
  border-top: 1px solid rgba(0, 0, 0, 0.1);
}

.cancel-btn {
  color: #64748B;
}

.save-btn {
  box-shadow: 0 2px 10px rgba(25, 118, 210, 0.3);
}

/* Menu de Ações */
.action-btn {
  transition: all 0.3s ease;
}

.action-btn:hover {
  transform: scale(1.1);
  background-color: rgba(25, 118, 210, 0.1) !important;
}

.action-menu {
  border-radius: 8px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.12);
  padding: 8px 0;
}

.menu-item {
  min-height: 40px;
  cursor: pointer;
  transition: background-color 0.2s;
}

.menu-item:hover {
  background-color: #f5f7fa;
}

/* Estados Vazios */
.empty-state {
  padding: 40px 0;
  text-align: center;
  color: #64748B;
}

.empty-text {
  margin-top: 16px;
  font-size: 1.1rem;
  color: #475569;
}

/* Responsividade */
@media (max-width: 960px) {
  .function-management-container {
    padding: 16px;
  }
  
  .table-header {
    flex-direction: column;
    align-items: flex-start;
  }
  
  .search-field {
    margin-top: 16px;
    width: 100%;
    max-width: 100%;
  }
  
  .permissions-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 600px) {
  .dialog-content {
    padding: 16px;
  }
  
  .dialog-actions {
    flex-direction: column-reverse;
    gap: 8px;
  }
  
  .cancel-btn, .save-btn {
    width: 100%;
    margin: 0;
  }
}
</style>